import React from 'react';
import { chartSeriesColor } from 'Utils/styleTokens';
import { HiOutlineLocationMarker, HiOutlineIdentification } from 'react-icons/hi';
import { MdSecurity } from 'react-icons/md';
import { getDominantEntityIncidence } from 'DataDiscovery/discoverApi';

const getTablePanelData = (data, table, filterEntity, filterColumn, sampledRows) => {
  try {
    let filteredOverviewValue = {};
    let totalValue = 0;
    let filteredEntityTotal = 0;
    let dataValue = {
      children: [{
        name: table,
        hex: "#212630",
        children: []
      }]
    };
    let tableData;
    if (data && data[table]) {
      tableData = data[table];
    }
  
    if (!tableData) {
      return {
        dataValue,
        totalValue: 0,
        filteredOverviewValue: {},
        filteredEntityTotal: 0
      };
    }
    // console.log('getTablePanelData', tableData, sampledRows);
  
    const tableDataKeys = Object.keys(tableData);
  
    const colorValues = tableDataKeys.map((_, i) => chartSeriesColor(i));
    let colorIterator = 0;
  
    for (let field of tableDataKeys) {
      if (!filterColumn || field === filterColumn) {
        let fieldChild = {
          name: field,
          hex: colorValues[colorIterator],
          children: []
        };
  
        if (tableData[field].hasOwnProperty('constraints')) {
          fieldChild.constraints = tableData[field].constraints;
        }
        
        colorIterator++;
  
        let entities = tableData[field].entities;
        const {
          dominantEntity,
          dominantCount,
          isLowIncidence
        } = getDominantEntityIncidence(entities, sampledRows);

        if (!isLowIncidence && dominantEntity) {
          filteredOverviewValue[dominantEntity] =
            (filteredOverviewValue[dominantEntity] || 0) + dominantCount;
        }

        if (filterEntity !== 'ALL') {
          const c = entities[filterEntity];
          if (c) filteredEntityTotal += c;
        }

        for (let entity of Object.keys(entities)) {
          const count = entities[entity];

          totalValue += count;

          let entityChild = {
            name: entity,
            value: entities[entity],
            hex: colorValues[colorIterator],
            opacity: 0.75
          };

          if (filterEntity === entity || filterEntity === 'ALL') {
            fieldChild.children.push(entityChild);
          }
        }
        dataValue.children.push(fieldChild);
      }
    }
    return { dataValue, totalValue, filteredOverviewValue, filteredEntityTotal };
  } catch(e) {
    console.log(e);
    throw e;
  }
}

const getEntityIcon = (entity) => {
  if (entity === 'Address' ||
    entity === 'City' ||
    entity === 'Country' ||
    entity === 'Location' ||
    entity === 'Postal Code' ||
    entity === 'State' ||
    entity ===  'Street' ||
    entity === 'Zip Code'
  ) {
    return <HiOutlineLocationMarker className="mr-2" />;
  } else if (entity === 'Age' ||
    entity === 'Driver License' ||
    entity === 'Email' ||
    entity === 'Passport' ||
    entity === 'Person' ||
    entity === 'Phone' ||
    entity === 'PIN' ||
    entity === 'SSN'
  ) {
    return <HiOutlineIdentification className="mr-2" />;
  } else {
    return <MdSecurity className="mr-2" />;
  }
}

const getEntityFeedData = (entity, fields, filteredByCol, sortBy) => {
  let fieldsValue = {};

  for (let prop in fields) {
    let entities = fields[prop].entities;
    let entityTotal = 0;

    for (let entity in entities) {
      entityTotal += entities[entity];
    }

    // Include fields even when all counts are 0 as long as the discovery
    // service attached at least one entity label (metadata-only finding).
    const hasEntityLabel = entities && Object.keys(entities).length > 0;
    if (entityTotal > 0 || hasEntityLabel) {
      fieldsValue[prop] = {
        ...fields[prop],
        entityTotal: entityTotal > 0 ? entityTotal : (hasEntityLabel ? 1 : 0)
      }
    }
  }

  let fieldsKeysValue = !filteredByCol ? 
    Object.keys(fieldsValue) : 
    [filteredByCol];

  if (entity !== 'ALL') {
    fieldsKeysValue = fieldsKeysValue.filter(field =>
      fieldsValue[field] && entity in (fieldsValue[field].entities || {})
    );
  }

  switch (sortBy) {
    case 'col-name-asc':
      fieldsKeysValue.sort((a, b) => a.localeCompare(b));
      break;
    case 'col-name-desc':
      fieldsKeysValue.sort((a, b) => a.localeCompare(b)).reverse();
      break;
    case 'entity-count-asc':
      if (entity !== 'ALL') {
        fieldsKeysValue.sort((a, b) => 
          fieldsValue[a].entities[entity] - fieldsValue[b].entities[entity]
        );
      } else {
        fieldsKeysValue.sort((a, b) => 
          fieldsValue[a].entityTotal - fieldsValue[b].entityTotal
        );
      }
      break;
    case 'entity-count-desc':
      if (entity !== 'ALL') {
        fieldsKeysValue.sort((a, b) => 
          fieldsValue[b].entities[entity] - fieldsValue[a].entities[entity]
        );
      } else {
        fieldsKeysValue.sort((a, b) => 
          fieldsValue[b].entityTotal - fieldsValue[a].entityTotal
        );
      }
      break;
  }

  return { fieldsValue, fieldsKeysValue };
}

/**
 * Groups a flat `{[fieldName]: {entities, constraints?}}` map (the same shape
 * getEntityFeedData already consumes) into a nested tree by path ancestry,
 * for the field list's tree-view toggle. Two path grammars are recognised:
 *
 * - Dotted (JSON leaf paths, Mongo/ES nested fields): split on `.`. Plain
 *   `.split('.')` correctly handles bracket notation on its own —
 *   `employees[*].ssn` splits to `['employees[*]', 'ssn']` since `[*]`
 *   contains no `.` — no special-casing needed.
 * - XPath-style (XML leaf paths, always `/`-rooted: `/catalog/book/@id`):
 *   split on `/`, dropping the empty segment before the leading slash.
 *
 * Every node carries independent `children`/`leafData` slots (rather than a node
 * being exclusively a branch or a leaf) so a path prefix that is itself both a
 * leaf finding and an ancestor of deeper paths can render both — a real
 * possibility for JSON-shaped data, not just a defensive edge case.
 */
const buildFieldPathTree = (fields) => {
  const root = { children: {}, leafData: null };
  for (const fieldName of Object.keys(fields || {})) {
    const segments = fieldName.startsWith('/')
      ? fieldName.split('/').filter(Boolean)
      : fieldName.split('.');
    let node = root;
    segments.forEach((seg, i) => {
      if (!node.children[seg]) {
        node.children[seg] = { children: {}, leafData: null };
      }
      node = node.children[seg];
      if (i === segments.length - 1) {
        node.leafData = { field: fieldName, data: fields[fieldName] };
      }
    });
  }
  return root;
};

export {
  getTablePanelData,
  getEntityIcon,
  getEntityFeedData,
  getDominantEntityIncidence,
  buildFieldPathTree
};